import { getBlob, ref } from 'firebase/storage';
import type { Item, ItemImage } from '@easymoving/types';
import { firebaseStorage } from '../firebase/init';

/**
 * Owner-only image loader.
 *
 * Firestore contains Storage paths, never shareable download URLs. The signed-in
 * owner's browser downloads each object through the Firebase SDK (so Storage
 * Rules and the current Auth token are enforced), then exposes an in-memory
 * blob URL to React. A collaborator's read is denied and simply leaves the item
 * with its normal furniture-icon fallback.
 */
const cache = new Map<string, Promise<string>>();

function cacheKey(path: string, revision?: number): string {
  return `${path}@${revision ?? 0}`;
}

export function privateImageUrl(path: string, revision?: number): Promise<string> {
  const key = cacheKey(path, revision);
  const existing = cache.get(key);
  if (existing) return existing;

  const pending = getBlob(ref(firebaseStorage(), path))
    .then((blob) => URL.createObjectURL(blob))
    .catch((error) => {
      cache.delete(key);
      throw error;
    });
  cache.set(key, pending);
  return pending;
}

export function forgetPrivateImage(path: string): void {
  for (const [key, pending] of cache) {
    if (!key.startsWith(`${path}@`)) continue;
    cache.delete(key);
    void pending.then((url) => URL.revokeObjectURL(url)).catch(() => undefined);
  }
}

function storedImage(image: ItemImage): ItemImage {
  const { url: _url, thumbUrl: _thumbUrl, ...stored } = image;
  return stored;
}

export function imagesForStorage(images: readonly ItemImage[]): ItemImage[] {
  return images.map(storedImage);
}

async function hydrateImage(image: ItemImage): Promise<ItemImage> {
  const stored = storedImage(image);
  const displayPath = stored.displayPath ?? stored.storagePath;
  const thumbPath = stored.thumbPath ?? displayPath;
  const [url, thumbUrl] = await Promise.all([
    privateImageUrl(displayPath, stored.revision),
    privateImageUrl(thumbPath, stored.revision),
  ]);
  return { ...stored, url, thumbUrl };
}

export async function hydratePrivateImages(item: Item): Promise<Item> {
  const storedImages = imagesForStorage(item.images ?? []);
  const hydrated = await Promise.all(
    storedImages.map(async (image) => {
      try {
        return await hydrateImage(image);
      } catch {
        // Expected for shared workspaces: only the owner may read photos.
        return image;
      }
    }),
  );
  const primary = hydrated.find((image) => image.isPrimary) ?? hydrated[0];
  return {
    ...item,
    images: hydrated,
    primaryImageUrl: primary?.url,
    primaryThumbUrl: primary?.thumbUrl ?? primary?.url,
  };
}
